import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getCurrentEvent, getNextEvent, getFights, getCommunityStats } from '../services/api';
import FightCard from './FightCard';
import CountdownTimer from './CountdownTimer';
import '../styles/app/picks-demo.css';

const LockIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="4.5" y="10.5" width="15" height="10" rx="2.5" />
    <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
  </svg>
);

const BeltIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 9h4l2-3h8l2 3h4v6h-4l-2 3H8l-2-3H2z" />
    <circle cx="12" cy="12" r="2.6" />
  </svg>
);

const DEMO_PICKS_COUNT = 3; // Number of picks before showing headliner teaser

function DemoPicks() {
  const [event, setEvent] = useState(null);
  const [fights, setFights] = useState([]);
  const [headliner, setHeadliner] = useState(null);
  const [communityStats, setCommunityStats] = useState({});
  const [picks, setPicks] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentFightIndex, setCurrentFightIndex] = useState(0);
  const [showHeadlinerTeaser, setShowHeadlinerTeaser] = useState(false);

  useEffect(() => {
    fetchEventData();
  }, []);

  const fetchEventData = async () => {
    try {
      // Try to get current event first, fall back to next event
      let eventData = null;
      try {
        const currentRes = await getCurrentEvent();
        // Only a card still taking picks (open) or queued (draft) works for
        // the demo — a live or finished card would show "Picks are closed".
        if (currentRes.data && ['open', 'draft'].includes(currentRes.data.status)) {
          eventData = currentRes.data;
        }
      } catch (e) {
        // No current event, try next
      }

      if (!eventData) {
        try {
          const nextRes = await getNextEvent();
          eventData = nextRes.data;
        } catch (e) {
          // 404 = nothing upcoming; handled below
        }
      }

      if (!eventData) {
        setError('No upcoming events');
        setLoading(false);
        return;
      }

      setEvent(eventData);

      // Fetch fights; the main event is the headliner, demo fights come from
      // the rest of the main card, topped up from prelims/early prelims when
      // the card is only partially announced.
      const fightsRes = await getFights(eventData.id);
      const allFights = fightsRes.data || [];
      const byPosition = (a, b) => b.card_position - a.card_position; // Main event first
      const mainCardFights = allFights.filter(f => f.card_type === 'main').sort(byPosition);
      const undercardFights = [
        ...allFights.filter(f => f.card_type === 'prelim').sort(byPosition),
        ...allFights.filter(f => f.card_type === 'early').sort(byPosition),
      ];

      // Main card first, then undercard — the top of this list is the
      // headliner even if the main card hasn't been announced yet.
      const cardFights = [...mainCardFights, ...undercardFights];
      if (cardFights.length > 0) {
        setHeadliner(cardFights[0]);
        // Take the DEMO_PICKS_COUNT fights just below the headliner, then
        // reverse so the demo goes from lower card up
        const demoFights = cardFights.slice(1)
          .slice(0, DEMO_PICKS_COUNT)
          .reverse();
        setFights(demoFights);
      }

      // Community pick % (non-blocking) — same data the real Picks screen shows
      getCommunityStats(eventData.id)
        .then(statsRes => {
          const statsMap = {};
          (statsRes.data || []).forEach(s => { statsMap[s.fight_id] = s; });
          setCommunityStats(statsMap);
        })
        .catch(() => { /* silent — demo still works without it */ });

      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch event data:', err);
      setError('Unable to load event');
      setLoading(false);
    }
  };

  const handlePick = (fightId, winner) => {
    setPicks(prev => ({
      ...prev,
      [fightId]: { ...prev[fightId], winner }
    }));
  };

  const handleMethodPick = (fightId, method) => {
    setPicks(prev => ({
      ...prev,
      [fightId]: { ...prev[fightId], method }
    }));

    // After method pick, move to next fight or show headliner teaser
    setTimeout(() => {
      if (currentFightIndex < fights.length - 1) {
        setCurrentFightIndex(prev => prev + 1);
      } else {
        setShowHeadlinerTeaser(true);
      }
    }, 400);
  };

  const handleRestart = () => {
    setPicks({});
    setCurrentFightIndex(0);
    setShowHeadlinerTeaser(false);
  };

  const completedPicks = Object.keys(picks).filter(id => picks[id]?.method).length;
  const totalSlots = fights.length + 1;
  const progress = totalSlots > 0 ? (completedPicks / totalSlots) * 100 : 0;

  if (loading) {
    return (
      <div className="pk-demo is-plain" aria-busy="true">
        <div className="pnp-skel pk-demo-skel-title" />
        <div className="pnp-skel pk-demo-skel-card" />
        <p className="pk-demo-note">Loading upcoming fights...</p>
      </div>
    );
  }

  if (error || (fights.length === 0 && !headliner)) {
    return (
      <div className="pk-demo is-plain">
        <p className="pk-demo-empty">
          {error || 'No fights available for demo'}
        </p>
        <Link to="/register" className="btn btn-primary">
          Get Started Free
        </Link>
      </div>
    );
  }

  const currentFight = fights[currentFightIndex];

  const renderLockedHeadliner = () => (
    <div className="pk-demo-locked">
      <div className="pk-demo-locked-card">
        <FightCard
          fight={headliner}
          pick={null}
          showMethod={false}
          disabled={true}
          isTitle={headliner.is_title_fight}
          featured
        />
      </div>
      <div className="pk-demo-lock">
        <div>
          <span className="pk-demo-lock-icon"><LockIcon /></span>
          <p>
            Create or join a pool to make your picks
          </p>
        </div>
      </div>
    </div>
  );

  // If we have a headliner but no demo fights (only the main event has been
  // announced so far), show the headliner locked behind a sign-up overlay.
  // Without this, currentFight is undefined and the demo render below
  // crashes on `currentFight.id`.
  if (!currentFight && headliner) {
    return (
      <div className="pk-demo is-gold">
        <div className="pk-demo-head">
          <div className="pk-demo-kicker is-gold">
            <BeltIcon /> Main Event
          </div>
          <h3 className="pk-demo-event">{event?.name}</h3>
          {event?.date && (
            <div className="pk-demo-countdown">
              <CountdownTimer targetDate={event.date} label="Picks close in" />
            </div>
          )}
        </div>

        {renderLockedHeadliner()}

        <div className="pk-demo-cta">
          <Link to="/register" className="btn btn-primary btn-lg">
            Sign up to make your picks
          </Link>
          <p className="pk-demo-note">
            More fights will be added as the card is announced.
          </p>
        </div>
      </div>
    );
  }

  // Headliner teaser screen - shown after completing demo picks
  if (showHeadlinerTeaser && headliner) {
    return (
      <div className="pk-demo is-gold">
        {/* Header */}
        <div className="pk-demo-head">
          <div className="pk-demo-kicker is-gold">
            <BeltIcon /> Main Event
          </div>
          <h3 className="pk-demo-event">{event?.name}</h3>
          {event?.date && (
            <div className="pk-demo-countdown">
              <CountdownTimer targetDate={event.date} label="Picks close in" />
            </div>
          )}
        </div>

        {/* Progress bar */}
        <div className="pk-demo-progress">
          <div style={{ width: `${progress}%` }} />
        </div>

        {/* Headliner Fight Card - Locked with overlay */}
        {renderLockedHeadliner()}

        {/* Demo picks summary */}
        <div className="pk-demo-summary">
          <div className="pk-demo-summary-title">
            Your Demo Picks
          </div>
          {fights.map((fight) => {
            const pick = picks[fight.id];
            const pickedFighter = pick?.winner;
            return (
              <div key={fight.id} className="pk-demo-summary-row">
                <span>{pickedFighter}</span>
                <span className="pk-demo-summary-method">
                  {pick?.method}
                </span>
              </div>
            );
          })}
        </div>

        {/* CTA buttons */}
        <div className="pk-demo-actions">
          <Link to="/register" className="btn btn-primary btn-lg pk-demo-actions-main">
            Create Account
          </Link>
          <button
            type="button"
            onClick={handleRestart}
            className="btn btn-secondary btn-lg"
          >
            Restart
          </button>
        </div>

        <p className="pk-demo-note">
          Create a pool, invite friends, and compete for real!
        </p>
      </div>
    );
  }

  return (
    <div className="pk-demo">
      {/* Header */}
      <div className="pk-demo-head">
        <div className="pk-demo-kicker">
          Try It Now
        </div>
        <h3 className="pk-demo-event">{event?.name}</h3>
        <p className="pk-demo-sub">
          Main Card • Pick <span className="pk-demo-num">{currentFightIndex + 1}</span> of <span className="pk-demo-num">{fights.length}</span>
        </p>
        {event?.date && (
          <div className="pk-demo-countdown">
            <CountdownTimer targetDate={event.date} label="Picks close in" />
          </div>
        )}
      </div>

      {/* Progress bar */}
      <div className="pk-demo-progress">
        <div style={{ width: `${progress}%` }} />
      </div>

      {/* Fight Card - Using actual FightCard component */}
      <div className="pk-demo-card">
        <FightCard
          fight={currentFight}
          pick={picks[currentFight.id]}
          onPick={(winner) => handlePick(currentFight.id, winner)}
          onMethodPick={(method) => handleMethodPick(currentFight.id, method)}
          showMethod={true}
          disabled={false}
          isTitle={currentFight.is_title_fight}
          communityStats={communityStats[currentFight.id]}
        />
      </div>

      {/* Picks summary dots - includes slot for headliner */}
      <div className="pk-demo-dots">
        {fights.map((fight, idx) => {
          const hasPick = picks[fight.id]?.method;
          const isCurrent = idx === currentFightIndex;
          return (
            <div
              key={fight.id}
              className={`pk-demo-dot ${hasPick ? 'is-done' : ''} ${isCurrent ? 'is-current' : ''}`}
            />
          );
        })}
        {/* Headliner dot - always last, locked appearance */}
        <div
          className="pk-demo-dot is-headliner"
          title="Main Event - Sign up to pick!"
        />
      </div>

      {/* Skip to signup */}
      <div className="pk-demo-skip-wrap">
        <Link to="/register" className="pk-demo-skip">
          Skip demo & sign up now
        </Link>
      </div>
    </div>
  );
}

export default DemoPicks;
